import { Controller } from "@hotwired/stimulus"

// Import only the icons we actually use (99KB → ~15KB bundle reduction)
import {
  LayoutDashboard,
  AlertCircle,
  Package,
  Users,
  ScrollText,
  CheckCircle,
  PanelLeftClose,
  Search,
  Ban,
  XCircle,
  Check,
  Trash,
  X,
  FileText,
  AlertTriangle,
  Megaphone,
  // Missing icons needed for theme toggle, navigation, and menus
  Sun,
  Moon,
  Menu,
  ChevronRight,
  ChevronDown,
  LogOut,
  User,
  Settings,
  SlidersHorizontal,
  Inbox,
  SearchX,
  ShieldAlert,
  ShieldCheck,
  MapPin,
  Send,
  Clock,
  Building2,
  UserCheck,
  ArrowRight,
  PawPrint,
  HeartHandshake,
  // Recommendation admin icons
  Sparkles,
  GitBranch,
  Activity,
  Info,
  MessagesSquare,
  MapPinned
} from 'lucide-static'

/**
 * Admin Icon Controller - Renders Lucide icons dynamically
 *
 * Optimized with named imports for 84% bundle size reduction
 *
 * Usage:
 * <span data-controller="admin-icon" data-admin-icon-name-value="layout-dashboard"></span>
 * <span data-controller="admin-icon" data-admin-icon-name-value="users" data-admin-icon-size-value="20"></span>
 */
export default class extends Controller {
  static values = {
    name: String,
    size: { type: Number, default: 24 },
    strokeWidth: { type: Number, default: 2 },
    color: { type: String, default: "currentColor" }
  }

  // Icon name mapping (kebab-case to PascalCase)
  static iconMap = {
    'layout-dashboard': LayoutDashboard,
    'alert-circle': AlertCircle,
    'package': Package,
    'users': Users,
    'scroll-text': ScrollText,
    'check-circle': CheckCircle,
    'panel-left-close': PanelLeftClose,
    'search': Search,
    'ban': Ban,
    'x-circle': XCircle,
    'check': Check,
    'trash': Trash,
    'x': X,
    'file-text': FileText,
    'alert-triangle': AlertTriangle,
    'megaphone': Megaphone,
    // Missing icon mappings
    'sun': Sun,
    'moon': Moon,
    'menu': Menu,
    'chevron-right': ChevronRight,
    'chevron-down': ChevronDown,
    'log-out': LogOut,
    'user': User,
    'settings': Settings,
    'sliders-horizontal': SlidersHorizontal,
    'inbox': Inbox,
    'search-x': SearchX,
    'shield-alert': ShieldAlert,
    'shield-check': ShieldCheck,
    'map-pin': MapPin,
    'send': Send,
    'clock': Clock,
    'building-2': Building2,
    'user-check': UserCheck,
    'arrow-right': ArrowRight,
    'paw-print': PawPrint,
    'heart-handshake': HeartHandshake,
    // Recommendation admin
    'sparkles': Sparkles,
    'git-branch': GitBranch,
    'activity': Activity,
    'info': Info,
    'messages-square': MessagesSquare,
    'map-pinned': MapPinned
  }

  connect() {
    this.renderIcon()

    // Listen for dynamic icon updates
    this.boundHandleUpdate = this.handleUpdate.bind(this)
    this.element.addEventListener("admin-icon:update", this.boundHandleUpdate)
  }

  disconnect() {
    this.element.removeEventListener("admin-icon:update", this.boundHandleUpdate)
  }

  renderIcon() {
    const iconName = this.nameValue
    const svgString = this.constructor.iconMap[iconName]

    if (svgString) {
      // lucide-static returns raw SVG strings, not objects with toSvg() method
      // Parse the SVG string and inject our custom attributes
      const parser = new DOMParser()
      const doc = parser.parseFromString(svgString, 'image/svg+xml')
      const svg = doc.querySelector('svg')

      if (svg) {
        // Apply custom attributes
        svg.setAttribute('width', this.sizeValue)
        svg.setAttribute('height', this.sizeValue)
        svg.setAttribute('stroke-width', this.strokeWidthValue)

        // Preserve existing classes and add new ones
        const existingClasses = this.element.className
        if (existingClasses) {
          svg.setAttribute('class', existingClasses)
        }

        // Set color (currentColor by default to inherit text color)
        svg.setAttribute('color', this.colorValue)

        // Replace element's content with the modified SVG
        this.element.innerHTML = svg.outerHTML
      } else {
        console.error(`Failed to parse SVG for icon "${iconName}"`)
      }
    } else {
      console.warn(`Icon "${iconName}" not found. Available icons:`, Object.keys(this.constructor.iconMap).join(', '))
    }
  }

  // Handle dynamic icon updates
  handleUpdate() {
    this.renderIcon()
  }

  // Value change callbacks
  nameValueChanged() {
    this.renderIcon()
  }
}
